// CreateSixService.ts
import type { CreateSixModalInformation, NewExternalUser } from "@/types/six/Six";
import type { SixOwner } from "@/types/six/Six";
import type { DeleteSixResponse } from "@/types/create-six.types";

export interface SixCreated {
  id: string;
  name: string;
}

export abstract class CreateSixService {
  /**
   * Checks whether a SIX name is already in use.
   * Should reject with an error that carries HTTP‐status `409`
   * (or a custom DuplicateNameError) if a duplicate is found.
   */
  abstract validateName(name: string): Promise<void>;

  /**
   * Creates the SIX in one call and returns its id & name.
   * Implementations should reject with validation errors
   * the same way `validateName` does, so the wizard can show them.
   */
  abstract createSix(payload: {
    name: string;
    description: string;
    ownerIds: SixOwner[];               // always at least one
    internalParticipantIds: string[]; // existing org users
    externalParticipantIds: string[]; // existing externals
    newExternalUsers: NewExternalUser[]; // brand‑new externals
  }): Promise<SixCreated>;

  /**
   * Updates an existing SIX.
   * @param id - The ID of the SIX to update
   * @param payload - The data to update
   */
  abstract updateSix(id: string, payload: {
    name: string;
    description: string;
    ownerIds: string[];
    internalParticipantIds: string[];
    externalParticipantIds: string[];
    newExternalUsers: NewExternalUser[];
    active?: 'true' | 'false';
  }): Promise<SixCreated>;

  /** Full list of org users for the "Internal participants" picker. */
  abstract getCreateSixModalInformation(): Promise<CreateSixModalInformation>;

  /**
   * Permanently deletes a SIX.
   * @param id - The ID of the SIX to delete
   * @returns DeleteSixResponse containing commandId for polling
   */
  abstract deleteSix(id: string): Promise<DeleteSixResponse>;
}
